import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import trackingApi from "./api/trackingApi";

// --- Models ---

export interface TrackingPosition {
  sessionId: string;
  latitude: number;
  longitude: number;
  accuracy: number | null;
  speed: number | null;
  heading: number | null;
  etaMinutes: number | null;
  recordedAt: string;
}

export interface TrackingSession {
  id: string;
  serviceRequestId: string;
  offerId: string;
  providerCustomerId: string;
  customerCustomerId: string;
  status: number;
  statusName: string;
  startedAt: string;
  endedAt: string | null;
  destinationLat: number | null;
  destinationLng: number | null;
  initialEtaMinutes: number | null;
  lastLat: number | null;
  lastLng: number | null;
  lastPositionAt: string | null;
  providerName: string;
  serviceRequestIssue: string | null;
  serviceRequestInternalNumber: string | null;
  latestPosition: TrackingPosition | null;
}

export interface TrackingState {
  activeSessions: TrackingSession[];
  positions: Record<string, TrackingPosition>;
  trackingStatus: Record<string, number>;
  loading: boolean;
  error: any;
}

// --- Thunks ---

export const fetchActiveSessions = createAsyncThunk(
  "tracking/fetchActiveSessions",
  async () => {
    return trackingApi.getActiveSessions();
  }
);

export const fetchSession = createAsyncThunk(
  "tracking/fetchSession",
  async (sessionId: string) => {
    return trackingApi.getSession(sessionId);
  }
);

// --- Slice ---

const initialState: TrackingState = {
  activeSessions: [],
  positions: {},
  trackingStatus: {},
  loading: false,
  error: undefined,
};

const trackingSlice = createSlice({
  name: "tracking",
  initialState,
  reducers: {
    updatePosition: (state, action: PayloadAction<TrackingPosition>) => {
      const position = action.payload;
      state.positions[position.sessionId] = position;
    },
    updateTrackingStatus: (
      state,
      action: PayloadAction<{ sessionId: string; status: number }>
    ) => {
      const { sessionId, status } = action.payload;
      state.trackingStatus[sessionId] = status;

      // Also update the session in activeSessions if it exists
      const session = state.activeSessions.find((s) => s.id === sessionId);
      if (session) {
        session.status = status;
      }
    },
    clearTracking: (state) => {
      state.activeSessions = [];
      state.positions = {};
      state.trackingStatus = {};
    },
    clearError: (state) => {
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchActiveSessions
      .addCase(fetchActiveSessions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchActiveSessions.fulfilled, (state, { payload }) => {
        state.activeSessions = payload;
        state.loading = false;

        // Populate positions and status maps from active sessions
        payload.forEach((session: TrackingSession) => {
          state.trackingStatus[session.id] = session.status;
          if (session.latestPosition) {
            state.positions[session.id] = session.latestPosition;
          }
        });
      })
      .addCase(fetchActiveSessions.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      // fetchSession
      .addCase(fetchSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSession.fulfilled, (state, { payload }) => {
        state.loading = false;

        // Update or add to activeSessions
        const index = state.activeSessions.findIndex(
          (s) => s.id === payload.id
        );
        if (index >= 0) {
          state.activeSessions[index] = payload;
        } else {
          state.activeSessions.push(payload);
        }

        state.trackingStatus[payload.id] = payload.status;
        if (payload.latestPosition) {
          state.positions[payload.id] = payload.latestPosition;
        }
      })
      .addCase(fetchSession.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });
  },
});

export const { updatePosition, updateTrackingStatus, clearTracking, clearError } =
  trackingSlice.actions;

export default trackingSlice.reducer;
